import React from 'react';
import { useEffect } from 'react';
import {
  UpdateScenePlaybackMetadataConnectorApollo,
  ToastProvider,
  OnMessageCacheModifier,
} from '@front-activerse/common-components';
import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
} from 'react-router-dom';
import allRoutes from './Routing/allRoutes';
import allRedirects from './Routing/allRedirects';
import PrivateRoute from './Routing/Components/PrivateRoute';
import RegularRoute from './Routing/Components/RegularRoute';
import ExternalRedirect from './Routing/Components/ExternalRedirect';
import 'react-toastify/dist/ReactToastify.css';
import SetupApp from './SetupApp';
import './stylesheet/brand.scss';
import 'animate.css/animate.min.css';
import { ErrorBoundary } from 'react-error-boundary';
import { FallbackErrorComponent, Error404 } from '@front-activerse/modules';
import { AppProvider } from '@front-activerse/common-components';
import images, { svgInline } from './assets/images';
import mainNavigation from './navigation/mainNavigation';
import userNavigation from './navigation/userNavigation';
import logoutNavigation from './navigation/logoutNavigation';
import footerNavigation from './navigation/footerNavigation';
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  LANDING_URL,
  APP_VERSION,
  SLIDE_ROUTES_DURATION,
  siteMetadata,
  INSTACART_FIREBASE_FUNCTION,
  INSTACART_URL,
  ALLOWED_LANGUAGES,
  APP_BRAND,
  FIRST_GOAL_SETTINGS_STEP_SLUG,
  FIRST_DIET_SETTINGS_STEP_SLUG,
} from './consts';

import preloader from './assets/lotties/preloader.json';
import loaderStars from './assets/lotties/loader_stars.json';

import { ForceUpdate } from '@front-activerse/common-components';
import { fetchRemoteConfigFields } from './Firebase/firebaseRemoteConfig';

const App = () => {
  useEffect(() => {
    const designSystem = global.localStorage.getItem('developerOptionsDesignSystem');
    if (designSystem && designSystem === 'master') {
      import('./stylesheet/main.scss');
    }
  }, []);

  const ErrorBoundaryLayout = () => (
    <ErrorBoundary FallbackComponent={FallbackErrorComponent}>
      <Outlet />
    </ErrorBoundary>
  );

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route element={<ErrorBoundaryLayout />}>
        {allRoutes.map((route) => {
          if (route.private) {
            return (
              <Route
                key={route.path}
                element={
                  <UpdateScenePlaybackMetadataConnectorApollo>
                    {({ updateScene }) => (
                      <PrivateRoute
                        title={route.title}
                        mobileAppTabIdentifier={route.mobileAppTabIdentifier}
                        updateScene={updateScene}
                        routePath={route.path}
                        descendantRoutes={route.descendantRoutes}
                      />
                    )}
                  </UpdateScenePlaybackMetadataConnectorApollo>
                }
              >
                <Route path={route.path} element={route.element} />
              </Route>
            );
          } else {
            return (
              <Route
                key={route.path}
                element={
                  <UpdateScenePlaybackMetadataConnectorApollo>
                    {({ updateScene }) => (
                      <RegularRoute
                        title={route.title}
                        mobileAppTabIdentifier={route.mobileAppTabIdentifier}
                        updateScene={updateScene}
                        routePath={route.path}
                        descendantRoutes={route.descendantRoutes}
                      />
                    )}
                  </UpdateScenePlaybackMetadataConnectorApollo>
                }
              >
                <Route path={route.path} element={route.element} />
              </Route>
            );
          }
        })}
        {allRedirects.map((redirect) => {
          if (redirect.externalRedirectTo) {
            return (
              <Route
                key={redirect.path}
                path={redirect.path}
                element={<ExternalRedirect to={redirect.externalRedirectTo} />}
              />
            );
          } else if (redirect.redirectTo) {
            return (
              <Route
                key={redirect.path}
                path={redirect.path}
                element={<Navigate replace to={redirect.redirectTo} />}
              />
            );
          } else {
            throw new Error('Redirect must have redirectTo or externalRedirectTo');
          }
        })}
        <Route path="*" element={<Error404 />} />
      </Route>
    )
  );

  return (
    // TODO: przenieść appbrand i trackFirebaseEvent z Route Outlet do App Provider-a, sprawdzić czy style przeniesione do modułu będą działać
    <AppProvider
      images={images}
      svgInline={svgInline}
      lotties={{ preloader, loaderStars }}
      navigation={{ mainNavigation, userNavigation, logoutNavigation, footerNavigation }}
      consts={{
        APP_STORE_URL,
        GOOGLE_PLAY_URL,
        LANDING_URL,
        APP_VERSION,
        SLIDE_ROUTES_DURATION,
        siteMetadata,
        INSTACART_FIREBASE_FUNCTION,
        INSTACART_URL,
        ALLOWED_LANGUAGES,
        APP_BRAND,
        FIRST_GOAL_SETTINGS_STEP_SLUG,
        FIRST_DIET_SETTINGS_STEP_SLUG,
      }}
      functions={{ fetchRemoteConfigFields }}
    >
      <SetupApp />
      <ForceUpdate />
      <OnMessageCacheModifier />
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </AppProvider>
  );
};

export default App;
